"use client"

import React from "react"
import styled from "styled-components"
import { motion, AnimatePresence } from "framer-motion"
import ProgressIndicator from "./ProgressIndicator"
import { API_BASE } from "../config/api"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  min-height: 200px;
  max-width: 95vw;
  margin: 0 auto;
`

const ConversationItem = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: rgba(13, 12, 29, 0.6);
  border-radius: 12px;
  border: 1px solid var(--border);
  width: 100%;
`

const QueryText = styled.div`
  color: var(--primary-text);
  font-size: 1.1rem;
  line-height: 1.5;
`

const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  overflow-x: auto;
`

const ResultTable = styled.div`
  width: 100%;
  min-width: 800px;
  background: rgba(13, 12, 29, 0.8);
  border-radius: 8px;
  padding: 16px;
  
  table {
    width: 100%;
    border-collapse: collapse;
    white-space: nowrap;
    
    th, td {
      padding: 12px 16px;
      text-align: left;
      border-bottom: 1px solid var(--border);
    }
    
    th {
      color: var(--accent-blue);
      font-weight: 600;
      position: sticky;
      top: 0;
      background: rgba(13, 12, 29, 0.9);
      z-index: 1;
    }
    
    td {
      color: var(--primary-text);
    }

    tr:hover {
      background: rgba(79, 141, 251, 0.05);
    }
  }
`

const Visualization = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin-top: 16px;
`

const EmptyState = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px;
  text-align: center;
  color: var(--secondary-text);
  background: rgba(13, 12, 29, 0.6);
  border-radius: 12px;
  border: 1px solid var(--border);
  
  h3 {
    color: var(--primary-text);
    font-size: 1.5rem;
    margin: 0;
  }
  
  p {
    max-width: 500px;
    line-height: 1.6;
  }
`

const ConversationView = ({ conversation, isLoading, progressMessages }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  }

  if (conversation.length === 0 && !isLoading) {
    return (
      <EmptyState
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h3>No Conversations Yet</h3>
        <p>
          Start by asking a question about the logistics data. You can analyze shipments,
          track costs, or explore trends over time.
        </p>
      </EmptyState>
    )
  }

  return (
    <Container
      as={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <AnimatePresence mode="wait">
        {conversation.map((item, index) => (
          <ConversationItem key={index} variants={itemVariants}>
            <QueryText>{item.query}</QueryText>
            
            {isLoading && index === conversation.length - 1 && (
              <ProgressIndicator messages={progressMessages} />
            )}
            
            {item.result && (
              <ResultContainer>
                <ResultTable
                  dangerouslySetInnerHTML={{ __html: item.result.output.html }}
                />
                {item.result.visualizationPath && (
                  <Visualization
                    src={`${API_BASE}/download/${item.result.visualizationPath}`}
                    alt="Data Visualization"
                  />
                )}
              </ResultContainer>
            )}
          </ConversationItem>
        ))}
      </AnimatePresence>
    </Container>
  )
}

export default ConversationView
